import { useFlag } from '@calendly/feature-check-react'
import moment from 'moment-timezone'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  MEETINGS_VIEW_DATE_PARAM,
  Periods,
  UPCOMING_PRESET_OFF,
  UPCOMING_PRESET_PARAM,
} from 'constants/meetings'
import { useFilters } from 'hooks/meetings/filters'
import { useQueryParams } from 'hooks/use_query_params'
import { timezoneSelector } from 'selectors/current_user'

import {
  pinnedPickDateSelector,
  scrollAnchorDateSelector,
  setOpenMeetingDate,
  setPinnedPickDate,
  setScrollAnchorDate,
  setViewDateLabelPinned,
  viewDateLabelPinnedSelector,
} from '../meetings_page_slice'

const DATE_FORMAT = 'YYYY-MM-DD'
const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const normalizeViewDate = (value) => {
  if (typeof value !== 'string' || !DATE_PARAM_PATTERN.test(value)) return null
  const parsed = moment(value, DATE_FORMAT, true)
  return parsed.isValid() ? value : null
}

/**
 * Drives the meetings-page date selector (behind `sa_default_preset_toggle`).
 *
 * The picked date lives in the `view_date` URL param. When set, it forces the untoggled
 * (legacy split) view and re-centers the feed on that date — the data layer reads `viewDate`
 * as the pivot so it loads meetings before and after the selected day. Any preset toggle
 * replaces the whole query string and drops `view_date`, which resets the selector to today.
 *
 * `displayDate` is what the selector button shows: the picked date while a date pick is settling,
 * otherwise the scroll anchor (date group at the sticky-header line — same line scroll-to-date
 * uses), otherwise the feed pivot, otherwise today. Opening the right rail does not change it.
 */
export const useMeetingsViewDate = () => {
  const presetFlagOn = useFlag('sa_default_preset_toggle', false)
  const queryParams = useQueryParams()
  const dispatch = useDispatch()
  const timezone = useSelector(timezoneSelector)
  const scrollAnchorDate = useSelector(scrollAnchorDateSelector)
  const viewDateLabelPinned = useSelector(viewDateLabelPinnedSelector)
  const pinnedPickDate = useSelector(pinnedPickDateSelector)
  const { applyFilterParams } = useFilters()

  const today = useMemo(
    () => (timezone ? moment().tz(timezone) : moment()).format(DATE_FORMAT),
    [timezone],
  )

  const viewDate = useMemo(
    () => (presetFlagOn ? normalizeViewDate(queryParams[MEETINGS_VIEW_DATE_PARAM]) : null),
    [presetFlagOn, queryParams],
  )

  const displayDate = (viewDateLabelPinned && (pinnedPickDate || viewDate))
    || scrollAnchorDate
    || viewDate
    || today

  const selectDate = useCallback(
    (date) => {
      if (!presetFlagOn) return
      const normalized = normalizeViewDate(date)
      if (!normalized) return
      dispatch(setPinnedPickDate(normalized))
      dispatch(setViewDateLabelPinned(true))
      dispatch(setScrollAnchorDate(normalized))
      dispatch(setOpenMeetingDate(null))
      // Picking a date is the untoggled, centered-on-date view: drop filters/presets (clean view),
      // set the off marker so no preset reads as active, and persist the picked date.
      dispatch(applyFilterParams({
        period: Periods.UPCOMING,
        [UPCOMING_PRESET_PARAM]: UPCOMING_PRESET_OFF,
        [MEETINGS_VIEW_DATE_PARAM]: normalized,
      }))
    },
    [presetFlagOn, dispatch, applyFilterParams],
  )

  return { presetFlagOn, viewDate, displayDate, today, timezone, selectDate }
}
